import {useEffect, useRef, useState} from 'react';

import axios, {CancelTokenSource} from 'axios';
import {useTranslation} from 'react-i18next';

import {
    CodeExportJob,
    CodeExportRequest,
    createCodeExport,
    downloadCodeExport,
    getCodeExport,
} from 'api/orderExport';

export function useCodeExportJob(onGenerated: (request: CodeExportRequest) => void) {
    const {t} = useTranslation();
    const [busy, setBusy] = useState(false);
    const [canResume, setCanResume] = useState(false);
    const [phase, setPhase] = useState('');
    const [error, setError] = useState('');
    const active = useRef<CancelTokenSource | null>(null);
    const saved = useRef<{
        request: CodeExportRequest;
        job: CodeExportJob;
        reported: boolean;
    } | null>(null);
    const wake = useRef<(() => void) | null>(null);

    const cancel = () => {
        active.current?.cancel();
        active.current = null;
        wake.current?.();
        saved.current = null;
        setCanResume(false);
        setBusy(false);
        setPhase('');
        setError('');
    };

    useEffect(
        () => () => {
            active.current?.cancel();
            active.current = null;
            wake.current?.();
        },
        [],
    );

    const run = async (payload: CodeExportRequest) => {
        if (active.current) return false;
        const source = axios.CancelToken.source();
        active.current = source;
        setBusy(true);
        setError('');
        try {
            if (!saved.current || saved.current.job.status === 'FAILED') {
                setPhase('submitting');
                const job = await createCodeExport(payload, source.token);
                source.token.throwIfRequested();
                if (!job.jobId) throw new Error(t('dictionaries.orders.exportJob.failed'));
                saved.current = {request: {...payload}, job, reported: false};
                setCanResume(job.status !== 'FAILED');
            }
            const {current} = saved;
            while (current.job.status !== 'DONE') {
                source.token.throwIfRequested();
                if (current.job.status === 'FAILED')
                    throw new Error(current.job.error || t('dictionaries.orders.exportJob.failed'));
                if (!['PENDING', 'PROCESSING'].includes(current.job.status))
                    throw new Error(t('dictionaries.orders.exportJob.failed'));
                setPhase(current.job.status);

                // eslint-disable-next-line no-await-in-loop
                await new Promise<void>((resolve) => {
                    const finish = () => {
                        clearTimeout(timer);
                        wake.current = null;
                        resolve();
                    };
                    const timer = setTimeout(finish, 2000);
                    wake.current = finish;
                });
                source.token.throwIfRequested();
                // eslint-disable-next-line no-await-in-loop
                current.job = await getCodeExport(current.job.jobId, source.token);
            }
            source.token.throwIfRequested();
            if (!current.reported) {
                current.reported = true;
                onGenerated(current.request);
            }
            setPhase('downloading');
            await downloadCodeExport(current.job, current.request, source.token);
            source.token.throwIfRequested();
            saved.current = null;
            setCanResume(false);
            return true;
        } catch (cause) {
            if (!source.token.reason)
                setError((cause as Error).message || t('dictionaries.orders.exportJob.failed'));
            return false;
        } finally {
            if (active.current === source) {
                active.current = null;
                setBusy(false);
                setCanResume(!!saved.current && saved.current.job.status !== 'FAILED');
                setPhase('');
            }
        }
    };

    return {run, cancel, busy, canResume, phase, error};
}
